import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { DollarSign, ArrowUpRight, Phone } from "lucide-react";
import { toast } from "sonner";

import { get, post } from "@/lib/api-client";
import { newIdempotencyKey } from "@/lib/idempotency";
import { formatCurrency } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select"; // 🛠️ Imported Select
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import type { Wallet, WithdrawalRequest } from "@/types/wallet";

interface Form { 
  amount: string; 
  payment_method: string; 
  account_number: string; 
}

export function WithdrawalRequestPage() {
  const nav = useNavigate();
  const qc = useQueryClient();

  const wallet = useQuery({
    queryKey: ["wallet"],
    queryFn: () => get<Wallet>("/wallet/wallet"),
  });
  
  const config = useQuery({
    queryKey: ["active-config"],
    queryFn: () => get<any>("/admin/configuration/active"), 
  });

  const { register, handleSubmit, formState: { errors }, watch } = useForm<Form>({
    defaultValues: { payment_method: "bkash" } // Default method
  });
  
  const amountStr = watch("amount");
  const amount = Number(amountStr || 0);

  const submit = useMutation({
    mutationFn: (data: Form) =>
      post<WithdrawalRequest>("/wallet/wallet/withdrawals", data, { idempotencyKey: newIdempotencyKey() }),
    onSuccess: (r) => {
      toast.success(`Withdrawal #${r.id} submitted for review`);
      qc.invalidateQueries({ queryKey: ["wallet"] });
      qc.invalidateQueries({ queryKey: ["withdrawals"] });
      nav("/dashboard/wallet");
    },
  });

  if (wallet.isLoading || !wallet.data) return <Skeleton className="h-96 w-full rounded-xl" />;

  // 🛠️ The user's main available balance limits how much they can withdraw
  const maxBalance = Number(wallet.data.available);
  const insufficient = amount > maxBalance;

  // Calculate charge
  const chargePercent = Number(config.data?.["wallet.withdrawal_charge_percent"] || 0);
  const chargeAmount = amount * (chargePercent / 100);
  const payableAmount = amount - chargeAmount;

  return (
    <div className="max-w-xl mx-auto space-y-6 animate-fade-in">
      <button onClick={() => nav(-1)} className="text-sm font-semibold text-surface-500 hover:text-surface-900 transition-colors">
        ← Back to wallet
      </button>

      <div>
        <h1 className="text-2xl font-bold text-surface-900">Request Withdrawal</h1>
        <p className="text-sm text-surface-500 mt-1">
          Funds move to Frozen during admin review. Withdrawal charge is {chargePercent}%.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>New request</CardTitle>
          <div className="text-sm text-surface-600">
            Available: <span className="font-bold text-surface-900">{formatCurrency(maxBalance)}</span>
          </div>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit((d) => submit.mutate(d))} className="space-y-5">
            
            {/* 🛠️ NEW: Payment Method & Account Number Fields */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Select
                label="Receive money via"
                options={[
                  { value: "bkash", label: "bKash" },
                  { value: "nagad", label: "Nagad" },
                  { value: "cash", label: "Cash Collection" },
                ]}
                error={errors.payment_method?.message}
                {...register("payment_method", { required: "Method is required" })}
              />
              
              <Input
                label="Your Account Number"
                placeholder="e.g. 017XXXXXXX"
                leftIcon={<Phone className="h-4 w-4" />}
                error={errors.account_number?.message}
                {...register("account_number", { 
                  required: watch("payment_method") !== "cash" ? "Account number is required" : false 
                })}
              />
            </div>

            <Input
              label="Amount to withdraw (Tk)"
              type="number" step="0.01" min="1"
              leftIcon={<DollarSign className="h-4 w-4" />}
              placeholder="0.00"
              error={errors.amount?.message || (insufficient ? "Exceeds available balance" : undefined)}
              {...register("amount", {
                required: "Amount required",
                validate: (v) => Number(v) > 0 || "Must be positive",
              })}
            />

            {amount > 0 && !insufficient && (
              <div className="bg-surface-50 border border-surface-200 p-4 rounded-xl space-y-2 animate-fade-in">
                <div className="flex justify-between text-sm text-surface-600">
                  <span>Requested Amount:</span>
                  <span>{formatCurrency(amount)}</span>
                </div>
                {chargePercent > 0 && (
                  <div className="flex justify-between text-sm text-danger-600 font-medium">
                    <span>Charge ({chargePercent}%):</span>
                    <span>- {formatCurrency(chargeAmount)}</span>
                  </div>
                )}
                <div className="border-t border-surface-200 pt-2 flex justify-between font-bold text-surface-900 text-lg">
                  <span>You will receive:</span>
                  <span className="text-success-700">{formatCurrency(payableAmount)}</span>
                </div>
              </div>
            )}

            <div className="pt-2">
              <Button type="submit" size="lg" fullWidth
                      leftIcon={<ArrowUpRight className="h-4 w-4" />}
                      loading={submit.isPending}
                      disabled={insufficient || amount <= 0}>
                Confirm & Submit Request
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}